/** Types mirroring the backend SSE payloads (see apps/backend/app/chat.py). */

/** ADR 0018 Phase A sentinel — the `section_title` value used for the
 * per-document synthesis chunk. Mirrors
 * `choir_rag_core.enrich.synthesis.SYNTHESIS_SECTION`. The UI swaps
 * this for a friendly label rather than showing the raw sentinel. */
export const SYNTHESIS_SECTION = "__synthesis__";

/** Sentinel the chunker stores when a chunk has no `## ` heading above it
 * (`choir_rag_core.chunking`). It is a placeholder, not a title, so it must
 * never reach the reader: a spreadsheet or a headingless PDF would show a
 * citation list of five identical "(untitled)" rows. Treated as "no
 * heading" so callers fall back to the document title. */
export const UNTITLED_SECTION = "(untitled)";

/** Map a chunk's `section_title` to its display string. Synthesis
 * chunks render as "Document summary" so the operator can tell at a
 * glance that the hit is the per-doc overview rather than a real
 * `## ` section. Returns "" when there is no usable heading — callers
 * should fall back to the document title rather than print a placeholder. */
export function displaySectionTitle(raw: string | undefined | null): string {
  if (!raw) return "";
  if (raw === SYNTHESIS_SECTION) return "Document summary";
  if (raw === UNTITLED_SECTION) return "";
  return raw;
}

export interface Hit {
  n: number;
  id: string;
  title: string;
  url: string;
  source: string;
  acl: string[];
  score: number;
  /** Section heading (`##`) the chunk belongs to. Empty on legacy
   * indices. Synthesis chunks (ADR 0018 Phase A) carry the special
   * value `__synthesis__` — use `displaySectionTitle()` to render. */
  section_title?: string;
  /** LLM-written 1-2 sentence summary of the chunk (ADR 0012). Empty when
   * ingestion ran without `ENRICH_SUMMARIZE_ENABLED`. */
  chunk_summary?: string;
  /** Closed-taxonomy topic tags (ADR 0012). Empty when ingestion ran
   * without `ENRICH_TOPICS_ENABLED`. */
  topics?: string[];
  /** Explicit cross-references regex-extracted from the chunk body at
   * ingest (ADR 0018 Phase B). Empty when no `[[…]]` / `(see …)` /
   * `cf. …` patterns were found, or on pre-Phase-B indices. */
  cites?: string[];
  /** First ~200 chars of the chunk content — preview for the citation
   * popup. The full chunk is still in the LLM's context for the answer. */
  snippet?: string;
  /** Document kind marker — set by the `find_profiles` tool (Phase 3b)
   * to `"profile"` so MessageBubble + SourcePreviewPane render the
   * citation as a Booking Matcher consultant card instead of the
   * standard chunk preview. Other tools / direct retrieval leave it
   * unset; absent => standard chunk render. */
  kind?: "profile" | "sql" | "upload";
  /** Optional scope tags surfaced from the chunk index — booking-
   * matcher uses these to distinguish profile / ticket / statusmail. */
  scope_tags?: string[];
}

export interface RetrievalEvent {
  type: "retrieval";
  filter: string;
  groups: string[];
  hits: Hit[];
  /** Set when the backend's query-rewrite booster rewrote the user's
   * message into a standalone retrieval query (feature-flagged via
   * `QUERY_REWRITE_ENABLED`). Null / missing on first turns and when
   * the rewrite produced an empty result. */
  rewritten_query?: string | null;
}

export interface DeltaEvent {
  type: "delta";
  content: string;
}

export interface DoneEvent {
  type: "done";
}

/** Task #294 — emitted by the persistence wrapper just before `done`,
 * carrying the DB row id of the assistant message that was just
 * persisted. Lets the SPA wire the feedback buttons to a fresh turn
 * without re-fetching the conversation. */
export interface PersistedEvent {
  type: "persisted";
  message_id: number;
}

export interface ErrorEvent {
  type: "error";
  /** `access_check` is the live-Graph RBAC stage (ADR 0003). */
  stage?: "retrieval" | "generation" | "access_check";
  message: string;
}

/** ADR 0017 Phase 2 — emitted when the LLM dispatches a tool. The
 * `iteration` counter (0-indexed) lets the UI render multi-step tool
 * chains in order. */
export interface ToolCallEvent {
  type: "tool_call";
  id: string;
  name: "run_sql" | "search_content" | string;
  arguments: Record<string, unknown>;
  iteration?: number;
}

/** ADR 0017 Phase 2 — emitted after the backend executes a tool call.
 * `ok=false` carries a `payload.error` string + `payload.kind` (e.g.
 * "sql_safety" / "execution") so the UI can render a friendly hint. */
export interface ToolResultEvent {
  type: "tool_result";
  id: string;
  name: string;
  ok: boolean;
  payload: Record<string, unknown>;
}

/** Emitted when the per-assistant workflow engine matched one or more
 * regex-routed workflows for the user's query (see
 * `apps/backend/app/workflows.py`). The matched workflow content was
 * injected into the system prompt; the UI uses this event to render a
 * "running <workflow>" badge on the assistant bubble so the operator
 * can see which framework the LLM is following. */
export interface WorkflowEvent {
  type: "workflow";
  matched: string[];
}

export type ChatEvent =
  | RetrievalEvent
  | DeltaEvent
  | DoneEvent
  | ErrorEvent
  | ToolCallEvent
  | ToolResultEvent
  | WorkflowEvent
  | PersistedEvent;

/** One LLM → tool → result round-trip, paired by `id`. The UI bubbles
 * up to the user as a single collapsible card under the assistant
 * message. */
export interface ToolRoundTrip {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  /** Undefined while the tool is still executing; set when the
   * `tool_result` arrives. */
  result?: ToolResultEvent;
}

export interface ChatTurn {
  id: string;
  role: "user" | "assistant";
  content: string;
  retrieval?: RetrievalEvent;
  error?: string;
  streaming?: boolean;
  /** ADR 0017 Phase 3 — every tool round-trip the LLM made on this
   * turn, in the order they fired. Empty for non-tool assistants. */
  tools?: ToolRoundTrip[];
  /** The user message that produced this turn — captured when the
   * assistant turn errors so the bubble can offer a "Try again"
   * button without the user re-typing their question. */
  retryMessage?: string;
  /** Workflow slugs the backend matched for this turn (system prompt
   * had their content injected). Empty/unset on turns where the
   * assistant has no workflows or no keyword fired. */
  matchedWorkflows?: string[];
  /** Task #294 — DB row id of the persisted assistant message. Only
   * set after the chat turn settles (the SSE done frame carries it);
   * the feedback endpoint needs this to scope the vote to a specific
   * message row. Undefined while the turn is streaming. */
  messageId?: number;
  /** Task #294 — caller's existing vote on this message, hydrated when
   * a past conversation is reopened. Undefined = no vote yet. */
  feedback?: {
    kind: "up" | "down";
    comment: string | null;
    updated_at?: string;
  };
}

export interface DevUser {
  email: string;
  label: string;
  expectedGroups: string[];
}
